import React, { useState, useMemo, useEffect } from "react";
import { Nav } from "react-bootstrap";
import { NavLink, useNavigate } from "react-router-dom";
import {
  House,
  BookOpen,
  MessageCircle,
  Trophy,
  LogOut,
  Menu,
  X,
  Gamepad2,
  FolderOpen,
  Sun,
  Moon,
} from "lucide-react";

import Logo from "../../fintogo_Wortbild_Logo_SVG.png";
import LanguageSwitcher from "./LanguageSwitcher";
import SubjectSwitcher from "./SubjectSwitcher";
import { useTranslation } from "../../hooks/useTranslation";
import { useTheme } from "../../contexts/ThemeContext";
import { getMe } from "../../api/client";
import { useSubject } from "../../contexts/SubjectContext";

const brandBlue = "#1E88E5";

// Rolle aus JWT holen
const getUserRole = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.user_type || payload.role;
  } catch (err) {
    console.error("Failed to decode token:", err);
    return null;
  }
};

export default function GlobalNav() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { darkMode, toggleDarkMode } = useTheme();
  const { validateSubjectForUser } = useSubject();
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);

  const userRole = useMemo(() => getUserRole(), [token]);

  useEffect(() => {
    if (token) {
      getMe().then((me) => { if (me) { setUser(me); validateSubjectForUser(me); } }).catch(() => {});
    }
  }, [token]);

  const handleLogout = () => {
    try {
      localStorage.removeItem("token");
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const resolveHomeTarget = () => {
    if (!token) return "/";
    if (userRole === "teacher") return "/TeacherDashboard";
    return "/StudentDashboard";
  };

  // Basis-NavItems für alle User
  const baseNavItems = [
    {
      to: resolveHomeTarget(),
      icon: House,
      label: t.nav.home,
      exact: true,
    },
    { to: "/Quizzes", icon: Trophy, label: t.nav.quizzes },
    { to: "/Lessons", icon: BookOpen, label: t.nav.lessons },
    { to: "/AiTutor", icon: MessageCircle, label: t.aiChat.title },

  ...(userRole === "teacher"
    ? [{ to: "/teacher/content", icon: FolderOpen, label: t.nav.manageContent }]
    : []),
  ];

  // fintogo Classroom-Link nur für Lehrer
  const teacherNavItems = userRole === "teacher"
    ? [{ to: "/teacher/livequiz", icon: Gamepad2, label: t.nav.liveQuiz }]
    : [];

  const navItems = [...baseNavItems, ...teacherNavItems];

  if (!token) return null;

  const NavItem = ({ to, icon: Icon, label, exact }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Nav.Link
        as={NavLink}
        to={to}
        end={exact}
        className="d-flex align-items-center gap-3 rounded mb-1 px-3 py-2"
        style={({ isActive }) => ({
          backgroundColor: isActive
            ? "var(--bg-active)"
            : isHovered
            ? "var(--bg-hover)"
            : "transparent",
          fontWeight: isActive ? "600" : "400",
          color: "var(--text-primary)",
          transition: "background-color 0.2s ease",
        })}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setMobileOpen(false)}
      >
        <Icon size={22} />
        <span>{label}</span>
      </Nav.Link>
    );
  };

  const AuthButton = ({ onClick, icon: Icon, label }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <button
        onClick={onClick}
        className="d-flex align-items-center gap-3 w-100 rounded px-3 py-2 mb-1"
        style={{
          backgroundColor: isHovered ? "rgba(0,0,0,0.1)" : "rgba(0,0,0,0.05)",
          color: "#333",
          backgroundColor: isHovered
            ? "var(--bg-active)"
            : "var(--bg-hover)",
          color: "var(--text-primary)",
          border: "none",
          cursor: "pointer",
          transition: "background-color 0.2s ease",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Icon size={22} />
        <span>{label}</span>
      </button>
    );
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        className="d-lg-none position-fixed"
        style={{
          top: 16,
          left: 16,
          zIndex: 1100,
          backgroundColor: "var(--bg-tertiary)",
          border: "none",
          borderRadius: 8,
          padding: "8px 12px",
          color: "var(--text-primary)",
        }}
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="d-lg-none position-fixed"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1040,
          }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <nav
        className={`d-flex flex-column position-fixed ${
          mobileOpen ? "d-flex" : "d-none d-lg-flex"
        }`}
        style={{
          top: 0,
          left: 0,
          bottom: 0,
          width: "240px",
          backgroundColor: "var(--bg-nav)",
          zIndex: 1050,
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {/* Brand / Logo */}
        <div
          className="d-flex align-items-center justify-content-center px-3 py-4"
          style={{ borderBottom: "1px solid var(--border-light)" }}
        >
          <button
            type="button"
            onClick={() => {
              navigate(resolveHomeTarget());
              setMobileOpen(false);
            }}
            className="d-flex align-items-center text-decoration-none bg-transparent border-0 p-0"
            style={{ cursor: "pointer" }}
          >
            <img
              src={Logo}
              alt="fintogo"
              style={{
                height: 64,
                width: "auto",
                maxWidth: "100%",
                objectFit: "contain",
              }}
            />
          </button>
        </div>

        {/* Navigation Links */}
        <Nav className="flex-column flex-grow-1 px-2 py-3">
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </Nav>

        {/* Auth Bereich */}
        <div
          className="px-2 py-3"
          style={{ borderTop: "1px solid var(--border-light)" }}
        >
          <div className="mb-2">
            <LanguageSwitcher />
          </div>

          <div className="mb-2">
            <SubjectSwitcher user={user} />
          </div>

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className="dark-mode-toggle mb-2"
            title={darkMode ? "Light Mode" : "Dark Mode"}
          >
            <div className="d-flex align-items-center gap-3">
              {darkMode ? <Moon size={22} /> : <Sun size={22} />}
              <span>{darkMode ? (t.settings?.darkMode || "Dark Mode") : (t.settings?.lightMode || "Light Mode")}</span>
            </div>
            <div className={`dark-mode-switch ${darkMode ? "active" : ""}`} />
          </button>

          {/* Logout Button */}
          <AuthButton onClick={handleLogout} icon={LogOut} label={t.auth.logout} />
        </div>
      </nav>
    </>
  );
}
